package ai.wagemix.web.theme

import com.varabyte.kobweb.compose.css.CSSLengthNumericValue
import com.varabyte.kobweb.compose.css.StyleVariable
import org.jetbrains.compose.web.css.CSSSizeValue
import org.jetbrains.compose.web.css.CSSUnit
import org.jetbrains.compose.web.css.ms
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vh
import org.jetbrains.compose.web.css.vw

/*
 * Spacing, sizing, shape and border tokens of the redesign (Penpot set `wagemix/core`), as
 * CSS variables so a page can retune them on `:root` without touching a component. Each
 * default is the token's value; the `…Compact` pairs are what the phone boards use. Roles
 * foundation already defines (margin, half margin, icon size, button corner) are read from
 * the FS variables directly.
 */

// Layout
val WMPageGutterVar by StyleVariable<CSSLengthNumericValue>(120.px)
val WMPageGutterCompactVar by StyleVariable<CSSLengthNumericValue>(16.px)
val WMContentWidthVar by StyleVariable<CSSLengthNumericValue>(1200.px)
val WMArticleWidthVar by StyleVariable<CSSLengthNumericValue>(720.px)
val WMSidebarWidthVar by StyleVariable<CSSLengthNumericValue>(248.px)
val WMAppColumnLeftVar by StyleVariable<CSSLengthNumericValue>(376.px)
val WMTopBarHeightVar by StyleVariable<CSSLengthNumericValue>(64.px)
/** The most the auth form and the auth brand preview may widen to (Penpot: 540 of a 720 form side). */
val WMAuthContentMaxWidthVar by StyleVariable<CSSLengthNumericValue>(560.px)
val WMPlanCardWidthVar by StyleVariable<CSSLengthNumericValue>(360.px)
val WMMixCardWidthVar by StyleVariable<CSSLengthNumericValue>(368.px)
val WMDialogWidthVar by StyleVariable<CSSLengthNumericValue>(480.px)
val WMDialogWidthWideVar by StyleVariable<CSSLengthNumericValue>(560.px)
// A sheet-sized dialog (the occupation picker) is relative to the viewport, never cut off: below
// Expanded the full width less the compact gutters, then these shares of the viewport width.
val WMSheetWidthExpandedVar by StyleVariable<CSSLengthNumericValue>(80.vw)
val WMSheetWidthLargeVar by StyleVariable<CSSLengthNumericValue>(60.vw)
// Its height is fixed by the viewport alone, so filtering never resizes it: a share of the viewport
// height (taller on phones, where it is a bottom sheet), never more than the viewport less the gutters.
val WMSheetHeightCompactVar by StyleVariable<CSSLengthNumericValue>(85.vh)
val WMSheetHeightVar by StyleVariable<CSSLengthNumericValue>(75.vh)
// The occupation picker as a bottom sheet on phones (Penpot `Compact · Browse Occupations`: 788 of 844): it leaves the
// top of the page in view.
val WMBottomSheetHeightVar by StyleVariable<CSSLengthNumericValue>(93.vh)
// The shared-mix link preview: the 1200×630 card the API snapshots (the social preview image size). Its summary
// column has a fixed width; the chart takes the rest, at the card's height less its padding.
val WMPreviewWidthVar by StyleVariable<CSSLengthNumericValue>(1200.px)
val WMPreviewHeightVar by StyleVariable<CSSLengthNumericValue>(630.px)
val WMPreviewPaddingVar by StyleVariable<CSSLengthNumericValue>(32.px)
val WMPreviewSummaryWidthVar by StyleVariable<CSSLengthNumericValue>(360.px)
val WMPreviewChartHeightVar by StyleVariable<CSSLengthNumericValue>(566.px)

// Section rhythm
val WMSectionPaddingBlockVar by StyleVariable<CSSLengthNumericValue>(96.px)
val WMSectionPaddingBlockCompactVar by StyleVariable<CSSLengthNumericValue>(56.px)
val WMHeroPaddingBlockVar by StyleVariable<CSSLengthNumericValue>(88.px)
val WMHeroPaddingBlockCompactVar by StyleVariable<CSSLengthNumericValue>(40.px)
val WMSectionContentGapVar by StyleVariable<CSSLengthNumericValue>(48.px)
val WMSectionContentGapCompactVar by StyleVariable<CSSLengthNumericValue>(32.px)
val WMHeadingGapVar by StyleVariable<CSSLengthNumericValue>(12.px)
val WMHeroStackGapVar by StyleVariable<CSSLengthNumericValue>(24.px)
val WMSplitGapVar by StyleVariable<CSSLengthNumericValue>(64.px)
val WMSplitGapCompactVar by StyleVariable<CSSLengthNumericValue>(32.px)
val WMBandPaddingVar by StyleVariable<CSSLengthNumericValue>(48.px)
val WMBandPaddingCompactVar by StyleVariable<CSSLengthNumericValue>(24.px)
val WMFooterPaddingBlockVar by StyleVariable<CSSLengthNumericValue>(48.px)
val WMFooterPaddingBlockCompactVar by StyleVariable<CSSLengthNumericValue>(32.px)
val WMFooterColumnsGapVar by StyleVariable<CSSLengthNumericValue>(64.px)
val WMFooterColumnsGapCompactVar by StyleVariable<CSSLengthNumericValue>(32.px)

// App shell
val WMAppContentPaddingVar by StyleVariable<CSSLengthNumericValue>(32.px)
val WMAppContentPaddingCompactVar by StyleVariable<CSSLengthNumericValue>(16.px)
val WMAppContentGapVar by StyleVariable<CSSLengthNumericValue>(24.px)
val WMAppContentGapCompactVar by StyleVariable<CSSLengthNumericValue>(16.px)
val WMTopBarPaddingBlockVar by StyleVariable<CSSLengthNumericValue>(16.px)
val WMHeaderPaddingBlockVar by StyleVariable<CSSLengthNumericValue>(16.px)
val WMHeaderActionsGapVar by StyleVariable<CSSLengthNumericValue>(12.px)
val WMSidebarPaddingVar by StyleVariable<CSSLengthNumericValue>(16.px)
val WMSidebarItemGapVar by StyleVariable<CSSLengthNumericValue>(4.px)
val WMSidebarItemPaddingInlineVar by StyleVariable<CSSLengthNumericValue>(12.px)
val WMSidebarItemPaddingBlockVar by StyleVariable<CSSLengthNumericValue>(8.px)
val WMSidebarItemIconGapVar by StyleVariable<CSSLengthNumericValue>(12.px)
val WMSidebarSectionGapVar by StyleVariable<CSSLengthNumericValue>(24.px)

// Cards, grids and clusters
val WMCardPaddingVar by StyleVariable<CSSLengthNumericValue>(24.px)
val WMCardPaddingCompactVar by StyleVariable<CSSLengthNumericValue>(16.px)
val WMCardGridGapVar by StyleVariable<CSSLengthNumericValue>(24.px)
val WMCardGridGapCompactVar by StyleVariable<CSSLengthNumericValue>(16.px)
val WMCardStackGapVar by StyleVariable<CSSLengthNumericValue>(16.px)
val WMCardTextGapVar by StyleVariable<CSSLengthNumericValue>(8.px)
val WMClusterGapVar by StyleVariable<CSSLengthNumericValue>(12.px)
val WMInlineGapVar by StyleVariable<CSSLengthNumericValue>(8.px)
val WMTightGapVar by StyleVariable<CSSLengthNumericValue>(4.px)
val WMHairGapVar by StyleVariable<CSSLengthNumericValue>(2.px)
val WMLabelGapVar by StyleVariable<CSSLengthNumericValue>(6.px)
val WMStatTileGapVar by StyleVariable<CSSLengthNumericValue>(4.px)
val WMListRowPaddingBlockVar by StyleVariable<CSSLengthNumericValue>(12.px)
val WMTableCellPaddingInlineVar by StyleVariable<CSSLengthNumericValue>(16.px)
val WMChartPaddingVar by StyleVariable<CSSLengthNumericValue>(20.px)
val WMNoticeIconGapVar by StyleVariable<CSSLengthNumericValue>(12.px)

// Controls
val WMControlHeightVar by StyleVariable<CSSLengthNumericValue>(40.px)
val WMControlSmHeightVar by StyleVariable<CSSLengthNumericValue>(32.px)
val WMControlCompactHeightVar by StyleVariable<CSSLengthNumericValue>(44.px)
val WMButtonPaddingInlineVar by StyleVariable<CSSLengthNumericValue>(16.px)
val WMButtonPaddingInlineSmVar by StyleVariable<CSSLengthNumericValue>(12.px)
val WMInputPaddingInlineVar by StyleVariable<CSSLengthNumericValue>(12.px)
val WMFieldLabelGapVar by StyleVariable<CSSLengthNumericValue>(8.px)
val WMPillPaddingInlineVar by StyleVariable<CSSLengthNumericValue>(10.px)
val WMPillPaddingBlockVar by StyleVariable<CSSLengthNumericValue>(2.px)
val WMPillGapVar by StyleVariable<CSSLengthNumericValue>(6.px)
val WMSegmentPaddingVar by StyleVariable<CSSLengthNumericValue>(4.px)
val WMCheckboxSizeVar by StyleVariable<CSSLengthNumericValue>(18.px)
val WMToggleWidthVar by StyleVariable<CSSLengthNumericValue>(36.px)
val WMToggleHeightVar by StyleVariable<CSSLengthNumericValue>(20.px)
val WMSliderThumbVar by StyleVariable<CSSLengthNumericValue>(18.px)
val WMSliderTrackVar by StyleVariable<CSSLengthNumericValue>(6.px)
val WMMeterHeightVar by StyleVariable<CSSLengthNumericValue>(6.px)
val WMDialogPaddingVar by StyleVariable<CSSLengthNumericValue>(24.px)
val WMDialogGapVar by StyleVariable<CSSLengthNumericValue>(20.px)

// Icons and media
val WMIconSizeVar by StyleVariable<CSSLengthNumericValue>(20.px)
val WMIconSmVar by StyleVariable<CSSLengthNumericValue>(16.px)
val WMIconLgVar by StyleVariable<CSSLengthNumericValue>(24.px)
val WMIconTileVar by StyleVariable<CSSLengthNumericValue>(40.px)
val WMAvatarVar by StyleVariable<CSSLengthNumericValue>(32.px)
val WMTierMedalVar by StyleVariable<CSSLengthNumericValue>(48.px)
val WMDropzoneHeightVar by StyleVariable<CSSLengthNumericValue>(200.px)
val WMChartHeightVar by StyleVariable<CSSLengthNumericValue>(360.px)
val WMChartHeightCompactVar by StyleVariable<CSSLengthNumericValue>(280.px)
val WMSparklineHeightVar by StyleVariable<CSSLengthNumericValue>(56.px)
val WMSparklineWidthVar by StyleVariable<CSSLengthNumericValue>(140.px)

// Shape
val WMControlCornerVar by StyleVariable<CSSLengthNumericValue>(8.px)
// A dropdown menu (WMMenuButton): wide enough for "Duplicate" with its icon, never narrower than its anchor's card needs.
val WMMenuMinWidthVar by StyleVariable<CSSLengthNumericValue>(180.px)
// A searchable dropdown's list (WMComboBox): about eight rows, then it scrolls.
val WMComboBoxListMaxHeightVar by StyleVariable<CSSLengthNumericValue>(320.px)
val WMCardCornerVar by StyleVariable<CSSLengthNumericValue>(12.px)
val WMPanelCornerVar by StyleVariable<CSSLengthNumericValue>(16.px)
val WMPillCornerVar by StyleVariable<CSSLengthNumericValue>(999.px)
val WMCheckboxCornerVar by StyleVariable<CSSLengthNumericValue>(4.px)

// Borders
val WMHairlineVar by StyleVariable<CSSLengthNumericValue>(1.px)
val WMFocusRingVar by StyleVariable<CSSLengthNumericValue>(2.px)
val WMIconStrokeVar by StyleVariable<Number>(1.75)

/** Opacity tokens (`wagemix.opacity.*`). */
object WMOpacity {
    const val BAND_OUTER = 0.12F
    const val BAND_INNER = 0.28F
    const val PROJECTION_WASH = 0.08F
    const val DISABLED = 0.5F
    /** The glow a guided tour's ring sends out from what it points at, as it starts: strong enough to catch the eye. */
    const val COACHMARK_GLOW = 0.85F
}

/** How long the landing fact carousel takes to move a fact into or out of the centre. */
val WMCarouselTransitionVar by StyleVariable<CSSSizeValue<CSSUnit.ms>>(500.ms)

/** One beat of the ring and glow around what a guided tour's step points at: brisk, like a heartbeat. */
val WMCoachmarkPulseVar by StyleVariable<CSSSizeValue<CSSUnit.ms>>(1200.ms)
/** The width of the ring around what a guided tour's step points at; heavier than a focus ring so it reads as a cue. */
val WMCoachmarkRingVar by StyleVariable<CSSLengthNumericValue>(3.px)
/** How far the ring swells away from what it points at at the height of each beat. */
val WMCoachmarkRingReachVar by StyleVariable<CSSLengthNumericValue>(10.px)
/** How far the glow spreads beyond what the ring points at before it has faded out. */
val WMCoachmarkGlowSpreadVar by StyleVariable<CSSLengthNumericValue>(24.px)

/** How long the phone drawer (the sidebar below Large) takes to slide in or out. */
val WMDrawerTransitionVar by StyleVariable<CSSSizeValue<CSSUnit.ms>>(200.ms)
